import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/ApiService';
import { ownerProfilePath } from '../../navigation/browseDestinations';
import { vehicleProfilePath, resolveVehicleNavSlug } from '../../utils/vehicleUrls';
import Button from '../ui/Button';
import { resolvePublicFileUrl } from '../../utils/imageUrls';
import type { HomeVehicleCard } from './homeTypes';

interface HomeMyGarageProps {
  vehicles: HomeVehicleCard[];
}

function vehicleTitle(vehicle: HomeVehicleCard): string {
  return (
    vehicle.display_name ||
    vehicle.nickname ||
    [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ') ||
    'Untitled build'
  );
}

function vehiclePath(vehicle: HomeVehicleCard, fallbackUsername: string): string {
  const slug = resolveVehicleNavSlug(vehicle);
  const username = (vehicle.owner_username || fallbackUsername || '').trim();
  if (username && slug) return vehicleProfilePath(username, slug);
  return ownerProfilePath(fallbackUsername);
}

/**
 * Compact garage rail above the signed-in feed.
 * Charcoal photo tiles match the pre-collectible home garage snapshot so
 * rarity foil / orange frame glow does not wash the page canvas.
 */
export const HomeMyGarage: React.FC<HomeMyGarageProps> = ({ vehicles }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const username = user?.username || '';
  const addPath = ownerProfilePath(username, { addVehicle: true });
  const isEmpty = vehicles.length === 0;

  const trackAdd = (surface: string) => {
    void apiService.trackEvent('home_cta_clicked', {
      metadata: { cta: isEmpty ? 'add_first_vehicle' : 'add_vehicle', surface },
    });
  };

  return (
    <section id="home-garage" className="mb-8 sm:mb-10" aria-labelledby="home-my-garage-heading">
      <div className="flex items-end justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h2
            id="home-my-garage-heading"
            className="font-display text-lg sm:text-xl font-semibold tracking-tight text-white"
          >
            My Garage
          </h2>
          <p className="mt-1 text-sm text-zinc-500">
            {isEmpty
              ? 'Add a build so specs, photos, and history stay with the car.'
              : `${vehicles.length} build${vehicles.length === 1 ? '' : 's'}`}
          </p>
        </div>
        <Button
          type="button"
          variant={isEmpty ? 'primary' : 'ghost'}
          size="sm"
          className="shrink-0 min-h-[40px]"
          onClick={() => {
            trackAdd('my_garage');
            navigate(addPath);
          }}
        >
          Add a build
        </Button>
      </div>

      {isEmpty ? (
        <div
          className="rounded-lg border border-dashed border-white/10 px-4 py-6"
          data-testid="home-my-garage-empty"
        >
          <p className="text-sm text-zinc-400">
            Your garage is empty. Start with one vehicle and keep its story in one place.
          </p>
        </div>
      ) : (
        <ul
          className="-mx-1 flex gap-3 overflow-x-auto pt-1 pb-3 px-1 snap-x snap-mandatory scrollbar-thin list-none m-0"
          data-testid="home-my-garage-rail"
        >
          {vehicles.map((vehicle) => {
            const title = vehicleTitle(vehicle);
            const path = vehiclePath(vehicle, username);
            const img = resolvePublicFileUrl(vehicle.image_url);
            const identity = [vehicle.year, vehicle.make, vehicle.model, vehicle.trim]
              .filter(Boolean)
              .join(' ');
            return (
              <li key={vehicle.id} className="snap-start shrink-0 w-36 sm:w-40">
                <Link
                  to={path}
                  className="group/tile block rounded-lg cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 transition-transform duration-200 hover:-translate-y-0.5 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
                  onClick={() => {
                    void apiService.trackEvent('home_cta_clicked', {
                      metadata: { cta: 'open_vehicle', surface: 'my_garage', vehicle_id: vehicle.id },
                    });
                  }}
                >
                  <div className="aspect-[4/3] rounded-lg overflow-hidden bg-[#0a0a0a] ring-1 ring-white/[0.08] mb-2 transition-[box-shadow] duration-200 group-hover/tile:ring-white/25 group-hover/tile:shadow-[0_10px_24px_-16px_rgba(0,0,0,0.8)] motion-reduce:group-hover/tile:shadow-none">
                    {img ? (
                      <img
                        src={img}
                        alt=""
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-200 group-hover/tile:scale-[1.06] motion-reduce:transition-none motion-reduce:group-hover/tile:scale-100"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[11px] text-zinc-600 transition-colors duration-200 group-hover/tile:text-zinc-400 group-hover/tile:bg-zinc-800/80">
                        No photo
                      </div>
                    )}
                  </div>
                  <p className="text-sm font-medium text-white truncate">{title}</p>
                  {identity && identity !== title ? (
                    <p className="mt-0.5 text-[11px] text-zinc-500 truncate transition-colors duration-200 group-hover/tile:text-zinc-400">
                      {identity}
                    </p>
                  ) : null}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default HomeMyGarage;
